import React, { useState } from 'react';
import { apiService } from '../services/api';

interface ShareDictionaryFormProps {
  dictionaryId: number;
  dictionaryName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const ShareDictionaryForm: React.FC<ShareDictionaryFormProps> = ({
  dictionaryId,
  dictionaryName,
  onSuccess,
  onCancel
}) => {
  const [recipientUsername, setRecipientUsername] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientUsername.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await apiService.shareDictionary(dictionaryId, recipientUsername.trim(), message.trim());
      setSuccess(true);
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share game');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-form">
        <h2>Game Shared Successfully!</h2>
        <div className="share-form-header">
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <p className="share-success-text">
            Game "{dictionaryName}" has been shared successfully!
          </p>
          <p className="share-countdown-text">
            Closing automatically in 2 seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <h2>Share Game</h2>
      <div className="share-form-header">
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📚</div>
        <p className="share-info-text">
          You're about to share:
        </p>
        <p className="share-dictionary-name">
          "{dictionaryName}"
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="recipient-username">Recipient Username:</label>
          <input
            type="text"
            id="recipient-username"
            value={recipientUsername}
            onChange={(e) => setRecipientUsername(e.target.value)}
            placeholder="Enter username"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="share-message">Message (optional):</label>
          <textarea
            id="share-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message..."
            disabled={loading}
            rows={3}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              padding: '10px',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Sharing...' : 'Share Game'}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              padding: '10px',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ShareDictionaryForm;